import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, X, Filter, HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FAQItem } from './FAQItem';
import type { FAQItem as FAQItemType } from '@/data/types';

interface CategoryOption {
  id: string;
  label: string;
}

interface FAQSearchProps {
  items: FAQItemType[];
  categories?: CategoryOption[];
  /** Optional title for the FAQ section */
  title?: string;
  /** Optional description */
  description?: string;
}

// Default categories if none provided
const defaultCategories: CategoryOption[] = [
  { id: 'all', label: 'All Questions' },
  { id: 'thresholds', label: 'Thresholds' },
  { id: 'measures', label: 'Measures' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'process', label: 'Process' },
  { id: 'communication', label: 'Communication' },
  { id: 'general', label: 'General' },
];

export function FAQSearch({
  items,
  categories = defaultCategories,
  title,
  description,
}: FAQSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  // Handle deep links on mount and hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash && items.find((item) => item.id === hash)) {
        setExpandedId(hash);
        // Clear any category filter to ensure the item is visible
        setSelectedCategory('all');
        setSearchQuery('');
        // Scroll to the item after a brief delay for render
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 150);
      }
    };

    // Check on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [items]);

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    return items.filter((item) => {
      // Category filter
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;

      if (!matchesCategory) return false;

      // Search filter (if query exists)
      if (query) {
        const matchesQuestion = item.question.toLowerCase().includes(query);
        const matchesResponse = item.response.toLowerCase().includes(query);
        const matchesKeyPoints = item.keyPoints.some((point) =>
          point.toLowerCase().includes(query)
        );
        return matchesQuestion || matchesResponse || matchesKeyPoints;
      }

      return true;
    });
  }, [items, searchQuery, selectedCategory]);

  // Count items per category for badges
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: items.length };
    items.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  }, [items]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleToggleItem = useCallback((id: string) => {
    setExpandedId((current) => {
      const newId = current === id ? null : id;
      // Update URL hash for deep linking
      if (newId) {
        window.history.replaceState(null, '', `#${newId}`);
      } else {
        window.history.replaceState(null, '', window.location.pathname);
      }
      return newId;
    });
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      {(title || description) && (
        <div className="text-center mb-8">
          {title && <h2 className="text-4xl font-bold text-eventus-navy mb-3">{title}</h2>}
          {description && <p className="text-lg text-gray-600 max-w-2xl mx-auto">{description}</p>}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
        <Input
          type="text"
          placeholder="Search questions, answers, or key points..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-14 pr-14 py-7 text-xl rounded-xl border-gray-200 focus:border-eventus-purple shadow-sm"
        />
        {searchQuery && (
          <button
            onClick={handleClearSearch}
            className="absolute right-5 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Clear search"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        )}
      </div>

      {/* Category filters toggle (mobile) */}
      <div className="flex items-center justify-between md:hidden">
        <Button
          variant="outline"
          size="default"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-base"
        >
          <Filter className="w-5 h-5" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
        <span className="text-base text-gray-500">
          {filteredItems.length} {filteredItems.length === 1 ? 'question' : 'questions'}
        </span>
      </div>

      {/* Category filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="-mx-2 px-2 overflow-hidden"
          >
            <div className="flex flex-wrap gap-3 py-2">
              {categories.map((category) => {
                const count = categoryCounts[category.id] || 0;
                const isSelected = selectedCategory === category.id;

                return (
                  <Badge
                    key={category.id}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all px-4 py-2 text-base ${
                      isSelected
                        ? 'bg-eventus-purple text-white shadow-md scale-105'
                        : 'hover:bg-eventus-purple/10 hover:border-eventus-purple/50'
                    }`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.label}
                    <span
                      className={`ml-2 text-sm ${
                        isSelected ? 'opacity-80' : 'opacity-60'
                      }`}
                    >
                      ({count})
                    </span>
                  </Badge>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results summary (desktop) */}
      <div className="hidden md:flex items-center justify-between text-base text-gray-500 border-b border-gray-100 pb-4">
        <span>
          Showing {filteredItems.length} of {items.length} questions
          {searchQuery && (
            <span>
              {' '}
              matching "<span className="font-medium text-eventus-purple">{searchQuery}</span>"
            </span>
          )}
        </span>
        {selectedCategory !== 'all' && (
          <button
            onClick={() => setSelectedCategory('all')}
            className="text-eventus-purple hover:underline text-base"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* FAQ items grouped by category */}
      <div className="space-y-8">
        <AnimatePresence mode="popLayout">
          {/* Group items by category when showing all */}
          {selectedCategory === 'all' && !searchQuery ? (
            // Render grouped by category with section dividers
            categories
              .filter((cat) => cat.id !== 'all')
              .map((category) => {
                const categoryItems = filteredItems.filter(
                  (item) => item.category === category.id
                );
                if (categoryItems.length === 0) return null;

                return (
                  <motion.section
                    key={category.id}
                    id={`category-${category.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="scroll-mt-28"
                  >
                    {/* Category Section Header */}
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
                      <h3 className="text-xl font-semibold text-eventus-navy">
                        {category.label}
                      </h3>
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {categoryItems.length} {categoryItems.length === 1 ? 'question' : 'questions'}
                      </span>
                    </div>

                    {/* Category Items */}
                    <div className="space-y-4">
                      {categoryItems.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ delay: Math.min(index * 0.05, 0.3) }}
                          layout
                        >
                          <FAQItem
                            item={item}
                            isExpanded={expandedId === item.id}
                            onToggle={() => handleToggleItem(item.id)}
                            highlightText={searchQuery}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </motion.section>
                );
              })
          ) : (
            // Render flat list when filtering or searching
            filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: Math.min(index * 0.05, 0.3) }}
                layout
              >
                <FAQItem
                  item={item}
                  isExpanded={expandedId === item.id}
                  onToggle={() => handleToggleItem(item.id)}
                  highlightText={searchQuery}
                />
              </motion.div>
            ))
          )}
        </AnimatePresence>

        {/* Empty state */}
        {filteredItems.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 px-4"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
              <HelpCircle className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No questions found</h3>
            <p className="text-lg text-gray-500 max-w-md mx-auto mb-4">
              {searchQuery
                ? `No questions match "${searchQuery}". Try a different search term or clear the filter.`
                : 'No questions in this category yet.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              {searchQuery && (
                <Button variant="outline" onClick={handleClearSearch}>
                  Clear Search
                </Button>
              )}
              {selectedCategory !== 'all' && (
                <Button variant="outline" onClick={() => setSelectedCategory('all')}>
                  Show All Categories
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
